import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import {
  ActivateProviderFaxLineRequest,
  FaxCoverPreviewPayload,
  FaxOriginals,
  FaxRecord,
  GetFaxesQuery,
  Page,
  ProviderFaxLine,
  ProviderFaxLineStatusResult,
  SendFaxPayload,
} from '@models';
import { HttpService } from '../http.service';
import { readContentDispositionFileName } from './fax-content-disposition';
import { FaxContent, FaxService } from './fax.service';

@Injectable({ providedIn: 'root' })
export class FaxApiService extends HttpService<never> implements FaxService {
  protected override readonly collectionPath = '/fax';

  private get faxesUrl(): string {
    return `${this.apiRootUrl}/faxes`;
  }

  getMyLine(): Observable<ProviderFaxLine> {
    return this.http.get<ProviderFaxLine>(`${this.apiUrl}/line`);
  }

  activateLine(areaCode?: string): Observable<ProviderFaxLine> {
    const body: ActivateProviderFaxLineRequest = areaCode ? { areaCode } : {};
    return this.http.post<ProviderFaxLine>(`${this.apiUrl}/line`, body);
  }

  getLineStatus(): Observable<ProviderFaxLineStatusResult> {
    return this.http.get<ProviderFaxLineStatusResult>(
      `${this.apiUrl}/line/status`
    );
  }

  releaseLine(): Observable<ProviderFaxLine> {
    return this.http.post<ProviderFaxLine>(`${this.apiUrl}/line/release`, {});
  }

  getFaxes(query: GetFaxesQuery): Observable<Page<FaxRecord>> {
    return this.http.get<Page<FaxRecord>>(this.faxesUrl, {
      params: this.getHttpParameters({ ...query }),
    });
  }

  getFax(id: string): Observable<FaxRecord> {
    return this.http.get<FaxRecord>(`${this.faxesUrl}/${id}`);
  }

  getFaxContent(id: string): Observable<FaxContent> {
    return this.http
      .get(`${this.faxesUrl}/${id}/content`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map(response => toFaxContent(response, id)));
  }

  getFaxOriginals(id: string): Observable<FaxOriginals> {
    return this.http.get<FaxOriginals>(`${this.faxesUrl}/${id}/originals`);
  }

  getFaxOriginalContent(id: string, position: number): Observable<FaxContent> {
    return this.http
      .get(`${this.faxesUrl}/${id}/originals/${position}`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map(response => toFaxContent(response, id)));
  }

  markFaxRead(id: string): Observable<FaxRecord> {
    return this.http.post<FaxRecord>(`${this.faxesUrl}/${id}/read`, {});
  }

  attachFaxToPatient(id: string, patientId: string): Observable<FaxRecord> {
    return this.http.post<FaxRecord>(`${this.faxesUrl}/${id}/attach`, {
      patientId,
    });
  }

  sendFax(payload: SendFaxPayload): Observable<FaxRecord> {
    return this.http.post<FaxRecord>(
      `${this.faxesUrl}/send`,
      toSendFormData(payload)
    );
  }

  resendFax(id: string): Observable<FaxRecord> {
    return this.http.post<FaxRecord>(`${this.faxesUrl}/${id}/resend`, {});
  }

  previewFaxCover(payload: FaxCoverPreviewPayload): Observable<Blob> {
    return this.http.post(`${this.faxesUrl}/cover-preview`, payload, {
      responseType: 'blob',
    });
  }
}

function toSendFormData(payload: SendFaxPayload): FormData {
  const form = new FormData();

  const append = (key: string, value: string | undefined): void => {
    if (value !== undefined && value !== null && value !== '') {
      form.append(key, value);
    }
  };

  append('toNumber', payload.toNumber);
  append('recipientName', payload.recipientName);
  append('recipientOrganization', payload.recipientOrganization);
  append('recipientPhone', payload.recipientPhone);
  append('reason', payload.reason);
  append('patientId', payload.patientId);
  append('subject', payload.subject);
  append('note', payload.note);
  append('senderName', payload.senderName);

  if (payload.senderPractice !== undefined) {
    form.append('senderPractice', payload.senderPractice);
  }

  form.append('acknowledgedLongFax', String(!!payload.acknowledgedLongFax));

  payload.files?.forEach(file => form.append('files', file, file.name));
  payload.documentIds?.forEach(id => form.append('documentIds', id));
  payload.faxRecordIds?.forEach(id => form.append('faxRecordIds', id));

  return form;
}

function toFaxContent(response: HttpResponse<Blob>, id: string): FaxContent {
  return {
    blob: response.body ?? new Blob(),
    fileName:
      readContentDispositionFileName(
        response.headers.get('content-disposition')
      ) ?? `fax-${id}.pdf`,
  };
}
